import {
  getConnectableIntegrationToolsForBrowseScenario,
  getConnectableIntegrationToolsForScenario,
  getIntegrationProviderForTool,
  normalizeIntegrationProvider,
  type ConnectableIntegrationScenario,
} from "../Settings/components/integrations/integrationCatalog";
import {
  groupOnboardingConnectionInstancesByProvider,
  getOnboardingConnectionKind,
  resolveOnboardingConnectionInstances,
  resolvePluginConnectionUsage,
  type OnboardingConnectionInstance,
  type OnboardingConnectionProviderGroup,
  type OnboardingServerIntegrationLike,
  type PluginConnectionUsage,
} from "./onboardingConnectionInstances";

export type { OnboardingServerIntegrationLike } from "./onboardingConnectionInstances";

export type OnboardingConnectionCategoryId =
  | "store_ops"
  | "advertising"
  | "email_marketing"
  | "marketing"
  | "analytics"
  | "seo"
  | "social_media"
  | "customers";

export type OnboardingConnectionBrowseCategoryId =
  OnboardingConnectionCategoryId;

export interface OnboardingConnectionCategory {
  id: OnboardingConnectionCategoryId;
  scenario: ConnectableIntegrationScenario;
}

export interface ResolveOnboardingConnectionRequirementsInput {
  serverIntegrations?: readonly OnboardingServerIntegrationLike[];
  persistedConnectedToolNames?: readonly string[];
  serverHydrated: boolean;
  requiredConnectionCount?: number;
  connectionRequirementBasis?: OnboardingConnectionRequirementBasis;
  requiresStoreConnection?: boolean;
  excludedCategoryIds?: readonly OnboardingConnectionCategoryId[];
  connectionCategories?: readonly OnboardingConnectionCategory[];
}

export interface OnboardingConnectionCategoryState extends OnboardingConnectionCategory {
  tools: string[];
  connectedTools: string[];
  count: number;
}

export interface OnboardingConnectionRequirementsState {
  categories: Record<
    OnboardingConnectionCategoryId,
    OnboardingConnectionCategoryState
  >;
  connectedTools: string[];
  connectedCount: number;
  connectedStoreCount: number;
  connectionInstances: OnboardingConnectionInstance[];
  connectionGroups: OnboardingConnectionProviderGroup[];
  connectionUsage: PluginConnectionUsage;
  requiredConnectionCount: number;
  remainingConnectionCount: number;
  meetsConnectionCount: boolean;
}

export type OnboardingPlanStepAccessState =
  | "skipped"
  | "checking-connections"
  | "blocked"
  | "ready";

export type OnboardingConnectionRequirementBasis =
  | "connected_tools"
  | "connection_instances"
  | "store_tools";

export interface CanContinueOnboardingConnectionStepInput {
  requirements: OnboardingConnectionRequirementsState;
  hasFreshServerSnapshot: boolean;
  hasPendingAccountSelection?: boolean;
  hasConnectionOperationInFlight?: boolean;
}

export interface ResolveOnboardingConnectionStepLoadingInput {
  isPluginOnboarding: boolean;
  hasFreshServerSnapshot: boolean;
  isStepSubmitting: boolean;
  isRefreshingConnections: boolean;
}

// BizSidekick Plugin gates onboarding on one valid connection instance.
// Multiple accounts on the same app still count separately for Plugin limits
// and progress, while the visual tool list remains app-deduped.
export const PLUGIN_ONBOARDING_MIN_CONNECTION_COUNT = 1;
export const BUSTLY_CORE_ONBOARDING_MIN_CONNECTION_COUNT = 1;

export const ONBOARDING_CONNECTION_CATEGORIES: readonly OnboardingConnectionCategory[] =
  [
    {
      id: "store_ops",
      scenario: "Store Ops",
    },
    {
      id: "advertising",
      scenario: "Advertising",
    },
    {
      id: "email_marketing",
      scenario: "Email Marketing",
    },
    {
      id: "analytics",
      scenario: "Analytics",
    },
    {
      id: "seo",
      scenario: "SEO",
    },
    {
      id: "social_media",
      scenario: "Social Media",
    },
    {
      id: "customers",
      scenario: "Customers",
    },
  ];

/**
 * Plugin onboarding follows the Admin Integrations navigation. It deliberately
 * excludes Social Media, Finance, Team Collaboration, and MCP from this
 * first-run flow because those categories have no supported Plugin connection
 * path yet.
 * Bustly Core keeps its own existing onboarding taxonomy above.
 */
export const PLUGIN_ONBOARDING_CONNECTION_CATEGORIES: readonly OnboardingConnectionCategory[] =
  [
    {
      id: "store_ops",
      scenario: "Store Ops",
    },
    {
      id: "advertising",
      scenario: "Advertising",
    },
    {
      id: "marketing",
      scenario: "Marketing",
    },
    {
      id: "analytics",
      scenario: "Analytics",
    },
    {
      id: "seo",
      scenario: "SEO",
    },
    {
      id: "customers",
      scenario: "Customers",
    },
  ];

// Facebook Page and Instagram remain available in Bustly Core and Admin Integrations,
// but they are intentionally outside the current BizSidekick Plugin onboarding catalog.
export const PLUGIN_ONBOARDING_EXCLUDED_CATEGORY_IDS: readonly OnboardingConnectionCategoryId[] =
  ["social_media"];

export const getOnboardingConnectionCategories = (
  isPluginOnboarding: boolean,
): readonly OnboardingConnectionCategory[] =>
  isPluginOnboarding
    ? PLUGIN_ONBOARDING_CONNECTION_CATEGORIES
    : ONBOARDING_CONNECTION_CATEGORIES;

export const getVisibleOnboardingConnectionCategories = (
  excludedCategoryIds: readonly OnboardingConnectionCategoryId[] = [],
  connectionCategories: readonly OnboardingConnectionCategory[] =
    ONBOARDING_CONNECTION_CATEGORIES,
): readonly OnboardingConnectionCategory[] => {
  const excludedCategoryIdSet = new Set(excludedCategoryIds);
  return connectionCategories.filter(
    (category) => !excludedCategoryIdSet.has(category.id),
  );
};

export const getOnboardingConnectionToolsForScenario = (
  scenario: ConnectableIntegrationScenario,
): string[] => getConnectableIntegrationToolsForScenario(scenario);

export const getOnboardingConnectionToolsForCategory = (
  category: OnboardingConnectionCategory,
): string[] =>
  category.id === "marketing"
    ? getConnectableIntegrationToolsForBrowseScenario(category.scenario)
    : getOnboardingConnectionToolsForScenario(category.scenario);

const normalizeToolName = (toolName?: string | null): string =>
  String(toolName || "")
    .trim()
    .toLowerCase();

const getConnectedServerProviders = (
  connectionInstances: readonly OnboardingConnectionInstance[],
): Set<string> =>
  new Set(
    connectionInstances
      .filter((instance) => instance.isValidConnection)
      .map((instance) => normalizeIntegrationProvider(instance.provider))
      .filter(Boolean),
  );

const getConnectedServerProviderList = (
  connectionInstances: readonly OnboardingConnectionInstance[],
): string[] =>
  connectionInstances
    .filter((instance) => instance.isValidConnection)
    .map((instance) => normalizeIntegrationProvider(instance.provider))
    .filter(Boolean);

const getCategoryProviderSet = (
  categories: readonly OnboardingConnectionCategory[],
): Set<string> =>
  new Set(
    categories
      .flatMap((category) => getOnboardingConnectionToolsForCategory(category))
      .map((tool) => getIntegrationProviderForTool(tool))
      .map((provider) => normalizeIntegrationProvider(provider))
      .filter(Boolean),
  );

const countVisibleConnectedInstances = (
  connectionInstances: readonly OnboardingConnectionInstance[],
  visibleCategories: readonly OnboardingConnectionCategory[],
): number => {
  const visibleProviders = getCategoryProviderSet(visibleCategories);

  return connectionInstances.filter(
    (instance) =>
      instance.isValidConnection &&
      visibleProviders.has(normalizeIntegrationProvider(instance.provider)),
  ).length;
};

const resolveCategoryState = (
  category: OnboardingConnectionCategory,
  connectedServerProviders: ReadonlySet<string>,
  connectedServerProviderList: readonly string[],
  persistedConnectedTools: ReadonlySet<string>,
  serverHydrated: boolean,
): OnboardingConnectionCategoryState => {
  const tools = getOnboardingConnectionToolsForCategory(category);
  const categoryProviders = new Set(
    tools
      .map((tool) => getIntegrationProviderForTool(tool))
      .map((provider) => normalizeIntegrationProvider(provider))
      .filter(Boolean),
  );
  const connectedTools = tools.filter((tool) => {
    const provider = getIntegrationProviderForTool(tool);
    const isConnectedOnServer = provider
      ? connectedServerProviders.has(normalizeIntegrationProvider(provider))
      : false;

    if (serverHydrated) {
      return isConnectedOnServer;
    }

    return (
      isConnectedOnServer ||
      persistedConnectedTools.has(normalizeToolName(tool))
    );
  });
  const count = serverHydrated
    ? connectedServerProviderList.filter((provider) =>
        categoryProviders.has(provider),
      ).length
    : connectedTools.length;

  return {
    ...category,
    tools,
    connectedTools,
    count,
  };
};

export const resolveOnboardingConnectionRequirements = ({
  serverIntegrations = [],
  persistedConnectedToolNames = [],
  serverHydrated,
  requiredConnectionCount = PLUGIN_ONBOARDING_MIN_CONNECTION_COUNT,
  connectionRequirementBasis = "connected_tools",
  requiresStoreConnection = false,
  excludedCategoryIds = [],
  connectionCategories = ONBOARDING_CONNECTION_CATEGORIES,
}: ResolveOnboardingConnectionRequirementsInput): OnboardingConnectionRequirementsState => {
  const connectionInstances =
    resolveOnboardingConnectionInstances(serverIntegrations);
  const connectionGroups =
    groupOnboardingConnectionInstancesByProvider(connectionInstances);
  const connectionUsage = resolvePluginConnectionUsage(connectionInstances);
  const connectedServerProviders =
    getConnectedServerProviders(connectionInstances);
  const connectedServerProviderList =
    getConnectedServerProviderList(connectionInstances);
  const persistedConnectedTools = new Set(
    persistedConnectedToolNames.map(normalizeToolName).filter(Boolean),
  );
  const excludedCategoryIdSet = new Set(excludedCategoryIds);
  const visibleCategories =
    getVisibleOnboardingConnectionCategories(excludedCategoryIds, connectionCategories);
  const categories = connectionCategories.reduce(
    (states, category) => {
      if (excludedCategoryIdSet.has(category.id)) {
        states[category.id] = {
          ...category,
          tools: [],
          connectedTools: [],
          count: 0,
        };
        return states;
      }
      states[category.id] = resolveCategoryState(
        category,
        connectedServerProviders,
        connectedServerProviderList,
        persistedConnectedTools,
        serverHydrated,
      );
      return states;
    },
    {} as Record<
      OnboardingConnectionCategoryId,
      OnboardingConnectionCategoryState
    >,
  );
  const connectedTools = Array.from(
    new Set(
      visibleCategories.flatMap(
        (category) => categories[category.id].connectedTools,
      ),
    ),
  );
  const connectedInstanceCount = serverHydrated
    ? countVisibleConnectedInstances(connectionInstances, visibleCategories)
    : connectedTools.length;
  const connectedCount =
    connectionRequirementBasis === "connection_instances"
      ? connectedInstanceCount
      : connectedTools.length;
  const visibleStoreProviders = new Set(
    categories.store_ops.tools
      .map((tool) => normalizeIntegrationProvider(getIntegrationProviderForTool(tool)))
      .filter((provider) => getOnboardingConnectionKind(provider) === "store"),
  );
  const connectedStoreCount = serverHydrated
    ? connectedServerProviderList.filter((provider) => visibleStoreProviders.has(provider)).length
    : categories.store_ops.connectedTools.filter((tool) =>
        visibleStoreProviders.has(normalizeIntegrationProvider(getIntegrationProviderForTool(tool))),
      ).length;
  const fulfilledConnectionCount = (() => {
    if (connectionRequirementBasis === "store_tools") {
      return connectedStoreCount;
    }

    if (connectionRequirementBasis === "connection_instances") {
      return connectedInstanceCount;
    }

    return connectedCount;
  })();
  const remainingConnectionCount = Math.max(
    0,
    requiredConnectionCount - fulfilledConnectionCount,
  );

  return {
    categories,
    connectedTools,
    connectedCount,
    connectedStoreCount,
    connectionInstances,
    connectionGroups,
    connectionUsage,
    requiredConnectionCount,
    remainingConnectionCount,
    meetsConnectionCount:
      remainingConnectionCount === 0 &&
      (!requiresStoreConnection || connectedStoreCount > 0),
  };
};

export const canContinueOnboardingConnectionStep = ({
  requirements,
  hasFreshServerSnapshot,
  hasPendingAccountSelection = false,
  hasConnectionOperationInFlight = false,
}: CanContinueOnboardingConnectionStepInput): boolean =>
  hasFreshServerSnapshot &&
  requirements.meetsConnectionCount &&
  !hasPendingAccountSelection &&
  !hasConnectionOperationInFlight;

/**
 * The Plugin connection step owns its initial snapshot loading state, but a
 * later background refresh must not take the Continue button back into a
 * spinner. Core keeps its existing refresh behavior.
 */
export const resolveOnboardingConnectionStepLoading = ({
  isPluginOnboarding,
  hasFreshServerSnapshot,
  isStepSubmitting,
  isRefreshingConnections,
}: ResolveOnboardingConnectionStepLoadingInput): boolean =>
  isStepSubmitting ||
  (isRefreshingConnections &&
    (!isPluginOnboarding || !hasFreshServerSnapshot));

export const resolveOnboardingPlanStepAccess = ({
  requirements,
  hasFreshServerSnapshot,
  shouldSkipConnectionStep,
}: {
  requirements: OnboardingConnectionRequirementsState;
  hasFreshServerSnapshot: boolean;
  shouldSkipConnectionStep: boolean;
}): OnboardingPlanStepAccessState => {
  if (shouldSkipConnectionStep) {
    return "skipped";
  }

  if (!hasFreshServerSnapshot) {
    return "checking-connections";
  }

  return requirements.meetsConnectionCount ? "ready" : "blocked";
};

export const resolveOnboardingStepForRendering = ({
  displayStep,
  planStepAccessState,
  preservePlanStep = false,
}: {
  displayStep: number;
  planStepAccessState: OnboardingPlanStepAccessState;
  preservePlanStep?: boolean;
}): number => {
  return displayStep === 2 && planStepAccessState === "blocked" && !preservePlanStep
    ? 1
    : displayStep;
};
